/* eslint-disable max-lines */
import { parseISO } from "date-fns";
import { Decimal } from "decimal.js";
import { z } from "zod";
import { AdvisorId, BasePermission, ClientId, Custodian, CustodianAccountId, DateSchema, Organization, PaginationMetadata, PhysicalAccountId, PortfolioPlanAssignmentId, PortfolioPlanId, SecurityId, TaxBudgetId, VirtualAccountId, } from "../Types";
export const OrderBySchema = z
    .enum(["createdAt asc", "createdAt desc"])
    .default("createdAt desc");
export const PortfolioPlanTags = z.enum([
    "DirectIndexedSleeve",
    "Taxable",
    "TaxExempt",
]);
export const PortfolioPlanLite = z.object({
    portfolioPlanId: PortfolioPlanId,
    name: z.string(),
    tags: PortfolioPlanTags.array(),
    version: z.number(),
    organization: Organization,
    createdAt: DateSchema,
    updatedAt: DateSchema,
    basePermission: BasePermission,
    activeClientCount: z.number().optional(),
    accountsAppliedCount: z.number().optional(),
    assignmentCount: z.number().optional(),
    dependentPlanCount: z.number().optional(),
    provider: z.string().optional(),
    numberOfSecurities: z.number().optional(),
});
export const LatestMetadata = z.object({
    latestAt: DateSchema,
    latestVersion: z.number(),
});
export const PublishMetadata = z.object({
    publishedAt: DateSchema,
    publishedVersion: z.number(),
});
export const PersonId = z.string().brand("PersonId");
export const PortfolioPlanResponse = z.object({
    data: z.array(z.object({
        plan: PortfolioPlanLite,
        publishMetadata: PublishMetadata.optional(),
    })),
    pagination: PaginationMetadata,
});
export const Expand = [
    "assignmentCount",
    "activeClientCount",
    "accountsAppliedCount",
    "dependentPlanCount",
    "publishMetadata",
    "provider",
    "numberOfSecurities",
];
export const OrderByOptions = [
    "createdAt asc",
    "createdAt desc",
    "updatedAt asc",
    "updatedAt desc",
    "name asc",
    "name desc",
    "assignmentCount asc",
    "assignmentCount desc",
];
export const PortfolioFamilyId = z.string().brand("PortfolioFamilyId");
// post portfolio plan types
const PortfolioWeight = z.string().transform((v) => new Decimal(v));
export const AssetClassId = z.string().uuid().brand("AssetClassId");
export const AssetClassCategory = z.enum([
    "Equity",
    "FixedIncome",
    "CashEquivalents",
    "Other",
]);
export const ClientOrAdvisorId = z.union([ClientId, AdvisorId]);
export const SecurityEntry = z.object({
    type: z.literal("Security"),
    securityId: SecurityId,
    weight: PortfolioWeight,
    assetClassId: AssetClassId.optional(),
    assetClassName: z.string().optional(),
    assetClassCategory: AssetClassCategory.optional(),
});
const NestedPlanObject = z.object({
    type: z.literal("NestedPlan"),
    planId: PortfolioPlanId,
    weight: PortfolioWeight,
    assetClassId: AssetClassId.optional(),
    assetClassName: z.string().optional(),
    assetClassCategory: AssetClassCategory.optional(),
});
const StrategicAllocationId = z.string().brand("StrategicAllocationId");
export const StrategicAllocationEntry = z.object({
    type: z.literal("StrategicAllocation"),
    strategicAllocationId: StrategicAllocationId,
    weight: PortfolioWeight,
    assetClassId: AssetClassId.optional(),
    assetClassName: z.string().optional(),
    assetClassCategory: AssetClassCategory.optional(),
});
export const Entry = z.union([
    SecurityEntry,
    NestedPlanObject,
    StrategicAllocationEntry,
]);
export const TransformerId = z.string().brand("TransformerId");
export const TransformerInputOutput = z.object({
    transformerId: TransformerId,
    metadata: z.any(),
});
export const ConcreteImplementationPlanIntent = z.enum([
    "TradingOn",
    "TradingOff",
]);
export const ConcreteImplementationPlanStatus = z.enum([
    "Inactive",
    "Active",
    "Liquidating",
    "FullyLiquidating",
    "Frozen_PendingLiquidationProposalApproval",
    "Frozen_PendingLiquidationTaskCompletion",
]);
export const StandardMaterialityFilter = z.object({
    weightThreshold: z.string(),
    arithmeticThreshold: z.string(),
});
export const Rebalancer = z.enum([
    "AlphaRebalancer",
    "TaxLossHarvestingOnlyRebalancer",
    "LiquidationRebalancer",
]);
export const RebalancerWithMetadata = z.object({
    rebalancer: Rebalancer,
    standardMaterialityFilter: StandardMaterialityFilter.optional(),
});
export const UserSuppliedRebalancerWithMetadata = z.object({
    rebalancer: Rebalancer.exclude(["LiquidationRebalancer"]),
    standardMaterialityFilter: StandardMaterialityFilter.optional(),
});
export const PortfolioAssignment = z.object({
    planAssignmentId: PortfolioPlanAssignmentId,
    portfolioPlanId: PortfolioPlanId,
    version: z.number(),
    name: z.string(),
    accounts: VirtualAccountId.array(),
    transformers: TransformerInputOutput.array(),
    intent: ConcreteImplementationPlanIntent,
    status: ConcreteImplementationPlanStatus,
    rebalancers: RebalancerWithMetadata.array(),
    createdAt: z.string().transform((x) => parseISO(x)),
    updatedAt: z.string().transform((x) => parseISO(x)),
});
export const CapitalConsequences = z.object({
    estimatedLongTermGainsDollars: z.string().transform((v) => new Decimal(v)),
    estimatedLongTermLossesDollars: z.string().transform((v) => new Decimal(v)),
    estimatedLongTermTaxesDollars: z.string().transform((v) => new Decimal(v)),
    estimatedShortTermGainsDollars: z.string().transform((v) => new Decimal(v)),
    estimatedShortTermLossesDollars: z.string().transform((v) => new Decimal(v)),
    estimatedShortTermTaxesDollars: z.string().transform((v) => new Decimal(v)),
});
// Match Vanadium/src/domains/Investment/TradeProposal/Types.ts
export const ProposalStatuses = [
    "Proposed",
    "Immaterial", //New for V2
    "Skipped",
    "Submitted",
    "Failed", // Not sure about this one, I would prefer "Error" if we want to wrap an error from pqs. This is presently the enum in the current system.
    "Canceled", // Not sure what to do with this, we may get rid of it in favor of the timestamps, or use it in conjunction with the timestamps
    "Superseded",
];
export const ProposalStatus = z.enum(ProposalStatuses);
export const TradeProposalId = z.string().brand("TradeProposalId");
export const OrderSchema = z.object({
    physicalAccountId: PhysicalAccountId,
    virtualAccountId: VirtualAccountId,
    custodianAccountId: CustodianAccountId,
    custodian: Custodian,
    securityId: SecurityId,
    quantity: z.string().transform((x) => new Decimal(x)),
    side: z.enum(["Buy", "Sell"]),
    orderType: z.literal("Market"),
    price: z.string().transform((x) => new Decimal(x)),
});
export const TradeProposal = z.object({
    status: z.enum(ProposalStatuses),
    proposalId: TradeProposalId,
    validFrom: z
        .string()
        .transform((x) => parseISO(x))
        .nullable(),
    expiresAt: z
        .string()
        .transform((x) => parseISO(x))
        .nullable(),
    submittedAt: z
        .string()
        .transform((x) => parseISO(x))
        .nullable(),
    createdAt: z.string().transform((x) => parseISO(x)),
    updatedAt: z.string().transform((x) => parseISO(x)),
    portfolioPlanAssignment: PortfolioAssignment,
    capitalConsequences: CapitalConsequences,
    trades: OrderSchema.array(),
    rebalancer: Rebalancer,
    pqsUniqueRebalanceId: z.string(),
});
export const TradeProposalBundleId = z.string().brand("TradeProposalBundleId");
export const TradeProposalBundleStatuses = [
    "Draft",
    "Staged",
    "Submitted",
    "TradingFailed",
];
export const TradeProposalBundleStatus = z.enum(TradeProposalBundleStatuses);
export const TradeProposalBundleType = z.enum([
    "Standard",
    "RequiresAdditionalReview",
]);
export const TradeProposalBundle = z.object({
    tradeProposalBundleId: TradeProposalBundleId,
    status: TradeProposalBundleStatus,
    version: z.number(),
    expiresAt: z.string().transform((x) => parseISO(x)),
    createdAt: z.string().transform((x) => parseISO(x)),
    updatedAt: z.string().transform((x) => parseISO(x)),
    tradeProposals: TradeProposalId.array(),
    type: TradeProposalBundleType,
});
export const PortfolioPlan = z.object({
    portfolioPlanId: PortfolioPlanId,
    name: z.string(),
    tags: PortfolioPlanTags.array(),
    version: z.number(),
    organization: Organization,
    createdAt: DateSchema,
    updatedAt: DateSchema,
    basePermission: BasePermission,
    ownerId: PersonId,
    entries: Entry.array(),
    transformers: TransformerInputOutput.array(),
    provider: z.string().optional(),
});
export const NestedPlanEntry = z.object({
    type: z.literal("NestedPlan"),
    planId: PortfolioPlanId,
    weight: z.string().transform((x) => new Decimal(x)),
    entries: z
        .intersection(z.union([
        SecurityEntry,
        StrategicAllocationEntry,
        z.lazy(() => NestedPlanEntry),
    ]), z.object({
        effectiveWeight: z.string().transform((x) => new Decimal(x)),
    }))
        .array(),
});
const TradeProposalPortfolioAssignment = PortfolioAssignment.extend({
    portfolioPlanVersion: z.number(), //AIP version
});
const TradeProposalPackId = z
    .string()
    .startsWith("TRPP_")
    .brand("TradeProposalPackId");
const PostPortfolioPackId = z.string().brand("PostPortfolioPackId");
export const TaxConsumption = z.object({
    taxBudgetAmount: z
        .string()
        .transform((x) => new Decimal(x))
        .optional(),
    netYtdLongTermGainOrLoss: z.string().transform((x) => new Decimal(x)),
    netYtdShortTermGainsOrLoss: z.string().transform((x) => new Decimal(x)),
    netYtdLongTermTaxes: z.string().transform((x) => new Decimal(x)),
    netYtdShortTermTaxes: z.string().transform((x) => new Decimal(x)),
    netYtdTaxBurden: z.string().transform((x) => new Decimal(x)),
});
export const TLHSwapProposedSchema = z.object({
    originalSecurity: SecurityId,
    replacementSecurity: SecurityId,
    concreteImplementationPlanId: PortfolioPlanAssignmentId,
    concreteImplementationPlanVersion: z.number(),
    tradeProposalId: TradeProposalId,
    proposedDate: DateSchema,
});
export const TLHSwapAcceptedSchema = z.object({
    originalSecurity: SecurityId,
    replacementSecurity: SecurityId,
    virtualAccountId: VirtualAccountId,
    tradeProposalId: TradeProposalId.nullable(),
    startDate: DateSchema,
    expireDate: DateSchema,
});
const FlattenedEntry = z.union([SecurityEntry, StrategicAllocationEntry]);
export const Holding = z.object({
    physicalAccountId: PhysicalAccountId,
    virtualAccountId: VirtualAccountId,
    custodianAccountId: CustodianAccountId,
    securityId: SecurityId,
    // Pre-Trade
    preQuantity: z.string().transform((x) => new Decimal(x)),
    preWeight: z.string().transform((x) => new Decimal(x)),
    preMarketValue: z.string().transform((x) => new Decimal(x)),
    // Post-Trade
    postQuantity: z.string().transform((x) => new Decimal(x)),
    postWeight: z.string().transform((x) => new Decimal(x)),
    postMarketValue: z.string().transform((x) => new Decimal(x)),
    // At time of proposal gen
    asOfPrice: z.string().transform((x) => new Decimal(x)),
    // Portfolio Model Weight
    portfolioWeight: z.string().transform((x) => new Decimal(x)),
    portfolioWeightDelta: z.string().transform((x) => new Decimal(x)),
});
export const UniqueRebalanceId = z.string().brand("UniqueRebalanceId");
export const TradeProposalBase = z.object({
    proposalId: TradeProposalId,
    pqsUniqueRebalanceId: UniqueRebalanceId,
    rebalancer: Rebalancer,
    status: ProposalStatus,
    validFrom: DateSchema.nullable(),
    expiresAt: DateSchema.nullable(),
    submittedAt: DateSchema.nullable(),
    createdAt: DateSchema,
    updatedAt: DateSchema,
    portfolioPlanAssignment: TradeProposalPortfolioAssignment, // CIP, with AIP id and AIP version
    trades: OrderSchema.array(), // This is the list of trades that will be executed
    version: z.number(),
    capitalConsequences: CapitalConsequences,
    proposedSwaps: TLHSwapProposedSchema.array(),
});
export const TradeProposalPack = z.object({
    proposalPackId: TradeProposalPackId,
    interdependentTradingBatchPackId: PostPortfolioPackId,
    proposals: TradeProposalBase.array(),
});
export const InvalidPositionsSchema = z.record(z.string(), z.literal("Not found"));
export class FinMasonError extends Error {
    constructor(message, invalidPositions) {
        super(message);
        this.name = "FinMasonError";
        this.invalidPositions = invalidPositions;
    }
}
export const FinMasonErrorResponse = z.object({
    message: z.string(),
    details: z.object({
        statusCode: z.literal("FIN_MASON_UNKNOWN_ASSET"),
        invalidPositions: InvalidPositionsSchema.optional(),
    }),
});
// schemas based on the Vanadium endpoint
// Vanadium/src/domains/Investment/TaxBudgets/schemas.ts
export const TaxBudgetSchemaResponse = z.object({
    taxBudgetId: z.string().transform((x) => TaxBudgetId.parse(x)),
    year: z.string(),
    budgetAmount: z.string().transform((x) => new Decimal(x)),
    clientIds: z
        .string()
        .array()
        .transform((array) => array.map((x) => ClientId.parse(x))),
    excludedVirtualAccountIds: z
        .string()
        .array()
        .transform((array) => array.map((x) => VirtualAccountId.parse(x))),
    createTime: DateSchema,
    modifyTime: DateSchema,
    deleteTime: DateSchema.optional(),
});
export const TaxBudgetGetQuerySchema = z.object({
    clientId: ClientId.array().optional(),
    year: z.number().optional(),
    isActive: z.boolean().optional(),
    page: z.number().optional(),
    pageSize: z.number().optional(),
    orderBy: OrderBySchema.optional(),
});
// Asset Classes and Portfolio Asset Classes
export const AssetClass = z.object({
    assetClassId: AssetClassId,
    assetClassName: z.string(),
    assetClassCategory: AssetClassCategory,
    ownerId: ClientOrAdvisorId.nullable(),
});
export const AssetClassList = AssetClass.array();
const AssetClassSecurityEntry = z.object({
    entryType: z.literal("Security"),
    entryTypeId: SecurityId,
});
const AssetClassPortfolioEntry = z.object({
    entryType: z.literal("Portfolio"),
    entryTypeId: PortfolioPlanId,
});
const PortfolioAssetClassEntry = z.union([
    AssetClassSecurityEntry,
    AssetClassPortfolioEntry,
]);
export const PortfolioAssetClass = z.object({
    assetClassId: AssetClassId,
    assetClassName: z.string(),
    assetClassCategory: AssetClassCategory,
    version: z.number(),
    entries: PortfolioAssetClassEntry.array(),
});
const PortfolioAssetClassWithId = z.object({
    assetClassId: AssetClassId,
    entries: PortfolioAssetClassEntry.array(),
});
const PortfolioAssetClassWithoutId = z.object({
    assetClassName: z.string(),
    assetClassCategory: AssetClassCategory,
    entries: PortfolioAssetClassEntry.array(),
});
const PortfolioAssetClassToSave = z.union([
    PortfolioAssetClassWithId,
    PortfolioAssetClassWithoutId,
]);
export const PostPortfolioAssetClassRequest = z.object({
    ownerId: ClientOrAdvisorId,
    assetClasses: PortfolioAssetClassToSave.array().refine((assetClasses) => assetClasses.length > 0, {
        message: "Need to include assetClasses when making a POST, cannot be an empty array",
    }),
});
export const PutPortfolioAssetClassRequest = z.object({
    ownerId: ClientOrAdvisorId,
    assetClasses: PortfolioAssetClassToSave.array(),
});
// Portfolio Billing Types
export const PortfolioBillingVersionId = z
    .string()
    .uuid()
    .brand("PortfolioBillingVersionId");
export const BillingVersion = z.object({
    portfolioBillingVersionId: PortfolioBillingVersionId,
    billingYear: z.number(),
    billingMonth: z.number(),
    createdAt: DateSchema,
    billingMode: z.enum(["monthly", "daily"]),
    asOfDate: DateSchema.optional(),
    availableDays: z.array(z.number()).optional(),
});
export const BillingVersionsResponse = z.object({
    data: BillingVersion.array(),
    pagination: PaginationMetadata,
});
//# sourceMappingURL=types.js.map